import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useAuth } from "@/features/auth/use-auth";
import { setTenantOverride } from "@/lib/auth-api";
import type { Tenant } from "@/lib/auth-types";
import { dropPersistedQueryCache } from "@/lib/query-persist";

export interface TenantSwitcherContextProps {
	selectedTenantId: number | null;
	selectedTenant: Tenant | null;
	isOverriding: boolean;
	setSelectedTenant: (tenant: Tenant | null) => void;
}

export const TenantSwitcherContext = React.createContext<TenantSwitcherContextProps | null>(null);

export function TenantSwitcherProvider({ children }: { children: React.ReactNode }) {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const [selectedTenant, setSelectedTenant] = React.useState<Tenant | null>(null);
	const selectedTenantId = selectedTenant?.tenantId ?? null;
	const mounted = React.useRef(false);

	const handleSetSelectedTenant = React.useCallback(
		(tenant: Tenant | null) => {
			setTenantOverride(tenant?.tenantId ?? null);
			setSelectedTenant(tenant);
			// Zapisany cache należy do POPRZEDNIEJ organizacji. Nie refetchuj jeszcze
			// aktywnego klucza: nagłówek wskazuje już nowego tenanta, a React wyrenderuje
			// nowy klucz dopiero po wyjściu z tego callbacku.
			dropPersistedQueryCache();
			const predicate = (query: { queryKey: readonly unknown[] }) =>
				query.queryKey[0] !== "tenants";
			queryClient.invalidateQueries({ predicate, refetchType: "none" });
		},
		[queryClient],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: selectedTenantId is the tenant-change signal; reset must run after React commits the new tenant header.
	React.useEffect(() => {
		if (!mounted.current) {
			mounted.current = true;
			return;
		}
		const predicate = (query: { queryKey: readonly unknown[] }) => query.queryKey[0] !== "tenants";
		void queryClient.resetQueries({ predicate });
	}, [queryClient, selectedTenantId]);

	const isOverriding = selectedTenantId !== null && selectedTenantId !== user?.tenantId;

	return (
		<TenantSwitcherContext.Provider
			value={{
				selectedTenantId,
				selectedTenant,
				isOverriding,
				setSelectedTenant: handleSetSelectedTenant,
			}}
		>
			{children}
		</TenantSwitcherContext.Provider>
	);
}
